// URL State Management
// Synchronizes application state with URL parameters
import { Logger } from './logger';
const console = new Logger('URLState');
function mustPercentEncodeQueryChar(ch, isKey) {
    const code = ch.codePointAt(0);
    if (code === undefined) {
        return false;
    }
    // C0 controls, space, and DEL cannot appear raw in a URL.
    if (code <= 0x20 || code === 0x7f) {
        return true;
    }
    // Query delimiters, percent-escape introducer, and '+' (form-urlencoded
    // space) must be escaped. '=' splits key from value.
    if (ch === '#' || ch === '&' || ch === '%' || ch === '+') {
        return true;
    }
    return isKey && ch === '=';
}
/**
 * Percent-encode only characters that would break the query string.
 * ASCII space becomes `+` in values so `text=hello+world` stays readable;
 * a literal `+` becomes `%2B`.
 */
export function encodeQueryComponent(value, isKey = false) {
    let encoded = '';
    for (const ch of value) {
        if (!isKey && ch === ' ') {
            encoded += '+';
            continue;
        }
        encoded += mustPercentEncodeQueryChar(ch, isKey)
            ? encodeURIComponent(ch)
            : ch;
    }
    return encoded;
}
export function serializeSearchParams(params) {
    const parts = [];
    for (const [key, value] of params) {
        parts.push(`${encodeQueryComponent(key, true)}=${encodeQueryComponent(value)}`);
    }
    return parts.join('&');
}
/** Absolute href using minimal query encoding (keeps `:`, `/`, `,`, Unicode). */
export function formatUrl(url) {
    const query = serializeSearchParams(url.searchParams);
    return `${url.origin}${url.pathname}${query ? `?${query}` : ''}${url.hash}`;
}
/**
 * Update URL with application state
 * Only updates parameters that are provided (partial update)
 */
export function updateUrlState(state) {
    const url = new URL(window.location.href);
    const searchParams = new URLSearchParams(url.search);
    // Update or remove parameters
    for (const [key, value] of Object.entries(state)) {
        if (value === null || value === undefined) {
            searchParams.delete(key);
        }
        else {
            searchParams.set(key, String(value));
        }
    }
    // Update URL without reload
    const query = serializeSearchParams(searchParams);
    const newUrl = `${url.pathname}${query ? '?' + query : ''}`;
    window.history.replaceState(null, '', newUrl);
    console.log('URL updated:', newUrl);
}
/**
 * Read application state from URL
 */
export function readUrlState() {
    const urlParams = new URLSearchParams(window.location.search);
    const state = {};
    // File
    const file = urlParams.get('file');
    if (file)
        state.file = file;
    // Mode
    const mode = urlParams.get('mode');
    if (mode === 'text' || mode === 'edit')
        state.mode = mode;
    // Text (`+` is a space; `%2B` is a literal plus)
    const text = urlParams.get('text');
    if (text)
        state.text = text;
    // Cursor
    const cursor = urlParams.get('cursor');
    if (cursor) {
        const cursorNum = parseInt(cursor, 10);
        if (!isNaN(cursorNum))
            state.cursor = cursorNum;
    }
    // Location
    const location = urlParams.get('location');
    if (location)
        state.location = location;
    // Features
    const features = urlParams.get('features');
    if (features)
        state.features = features;
    console.log('Read state from URL:', state);
    return state;
}
/**
 * Encode userspace location as URL parameter
 * Converts {wght: 400, wdth: 100} to "wght:400,wdth:100"
 * Values are rounded to integers
 */
export function encodeLocation(location) {
    return Object.entries(location)
        .map(([tag, value]) => `${tag}:${Math.round(Number(value))}`)
        .join(',');
}
/**
 * Decode userspace location from URL parameter
 * Converts "wght:400,wdth:100" to {wght: 400, wdth: 100}
 */
export function decodeLocation(encoded) {
    if (!encoded)
        return null;
    try {
        const result = {};
        const pairs = encoded.split(',');
        for (const pair of pairs) {
            const [tag, valueStr] = pair.split(':');
            const value = parseFloat(valueStr);
            if (!tag || isNaN(value)) {
                console.warn('Invalid location pair:', pair);
                continue;
            }
            result[tag] = value;
        }
        return Object.keys(result).length > 0 ? result : null;
    }
    catch (error) {
        console.error('Error decoding location:', error);
        return null;
    }
}
/**
 * Encode feature list as URL parameter
 * Converts ['liga', 'kern'] to "liga,kern"
 */
export function encodeFeatures(features) {
    return features.join(',');
}
/**
 * Decode feature list from URL parameter
 * Converts "liga,kern" to ['liga', 'kern']
 */
export function decodeFeatures(encoded) {
    if (!encoded)
        return null;
    return encoded.split(',').filter((f) => f.length > 0);
}
